import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import { FlowType } from 'webapp/services/flow/annual-plan';

interface Args {
	onClose: () => void;
}

export default class AppNotificationsYearlyUpgradeComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;
	@service('flow/annual-plan') declare flowAnnualPlanService: Service.Flow.AnnualPlan;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.analyticsService.event(TrackingEvent.ANNUAL_BANNER_SHOWN, {
			experiments: [this.flowAnnualPlanService.trackingOrigin],
		});
	}

	get targetPlan() {
		const currentPlanType = this.billingService.activePlan?.planType;
		return this.billingService.availablePlans
			.filter((item) => item.planType === currentPlanType)
			.find((item) => item.isYearly)!;
	}

	get discount() {
		return this.billingService.billingCycleDiscounts.yearly;
	}

	@action
	handleAction() {
		this.billingService.switchPlan(this.targetPlan, { billingPeriodChangePopup: 'top_banner' });
	}

	@action
	close(e: PointerEvent) {
		const el = $(e.target as HTMLElement).closest('.notification-container');
		el.fadeOut(1000);

		if (this.flowAnnualPlanService.isEligibleForRegularFlow) {
			this.flowAnnualPlanService.triggerSkipYearlyUpgradeNotification();
		} else {
			this.userSettingsService
				.save(UserSettingsKey.SKIP_YEARLY_UPGRADE_NOTIFICATION, 1)
				.then(() => {
					this.analyticsService.event(TrackingEvent.ANNUAL_BANNER_SKIPPED, {
						experiments: [FlowType.TEMPLATE],
					});
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}

		later(() => {
			this.args.onClose();
		}, 1000);
	}
	<template>
		<div class="notification-container yearly-upgrade-notification">
			<div class="container">

				<a role="button" class="close" {{on "click" this.close}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>

				<p>
					<CommonUiIconComponent @name="Percentage" @size="lg" />
					{{#if this.flowAnnualPlanService.isEligibleForRegularFlow}}
						{{{this.billingService.yearlyDiscountText}}}
					{{else}}
						<strong>Get {{this.discount}}% off </strong>
						monthly subscription rates.
					{{/if}}
				</p>
				<a class="button size-small" {{on "click" this.handleAction}}>Switch to annual</a>
			</div>
		</div>
	</template>
}
